'use client';

import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { useCookieConsent } from '@packages/shared/src/hooks/useCookieConsent/useCookieConsent';
import { Card, CardContent } from '@packages/shared/src/ui/Card/Card';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { type Brand } from '@packages/gql/generated/shopping/graphql';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import type { MessageValue } from 'react-intl';
import { useManufacturerTracking } from '../Tracking/hooks/useManufacturerTracking/useManufacturerTracking';

export type LoadbeeCardProps = {
  brand: Pick<Brand, 'name' | 'image'>;
  url: string;
};

export const LoadbeeCard = ({ brand, url }: LoadbeeCardProps) => {
  const isAllowed = useCookieConsent('C0004');
  const { formatMessage } = useIntl();
  const cardRef = useRef<HTMLDivElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { trackGlycerinButtonClick, trackGlycerinDisplayOverlay } = useManufacturerTracking({
    brandName: brand.name || '',
    ref: cardRef,
    vendor: 'Loadbee',
  });

  const onClickHandler = () => {
    setIsDialogOpen(true);
    trackGlycerinButtonClick();
    trackGlycerinDisplayOverlay();
  };

  if (!isAllowed) {
    return null;
  }

  return (
    <div>
      <Card ref={cardRef}>
        <CardContent
          className="flex cursor-pointer items-center gap-1.5 p-1.5"
          onClick={onClickHandler}
        >
          {brand.image && (
            <Image
              loading="lazy"
              src={brand.image}
              alt={brand.name}
              style={{ objectFit: 'contain' }}
              width={50}
              height={30}
            />
          )}
          <span>
            {formatMessage<{ readonly brand: MessageValue }>(
              {
                defaultMessage: 'Weitere Herstellerinformationen von {brand}',
                id: 'loadbee.label.text',
              },
              {
                brand: brand.name || '',
              },
            )}
          </span>
        </CardContent>
      </Card>
      <Dialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        className="h-full w-full md:h-[65%]"
      >
        <DialogHeader onClose={() => setIsDialogOpen(false)}>
          {formatMessage<{ readonly brand: MessageValue }>(
            {
              defaultMessage: 'Herstellerinformationen von {brand}',
              id: 'loadbee.modal.headline',
            },
            {
              brand: brand.name || '',
            },
          )}
        </DialogHeader>
        <DialogContent className="px-1">
          <iframe title="title" src={url} className="h-full w-full" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
